import * as Yup from 'yup'

// Allowed classification sets (design: Data Models; Req 1.5, 6.1)
export const URGENCIES = ['Low', 'Medium', 'High', 'Critical']
export const STATUSES = ['Open', 'Waiting for Support', 'Waiting for Customer', 'Closed']

// Environments a ticket can pertain to, and the products a customer may pick.
export const ENVIRONMENTS = ['UAT', 'Production']
export const PRODUCTS = ['MediPro', 'ACAPro']

export const isValidUrgency = (value) => URGENCIES.includes(value)
export const isValidStatus = (value) => STATUSES.includes(value)

// Resolve which products a customer may select from their feature-access flags.
// Mirrors the customer model: isMediproCustomer -> MediPro, isAcaproCustomer -> ACAPro.
export const availableProducts = ({ isMediproCustomer, isAcaproCustomer } = {}) => {
  const products = []
  if (isMediproCustomer !== false) products.push('MediPro')
  if (isAcaproCustomer) products.push('ACAPro')
  return products
}

// Build the ticket validation schema. `productOptions` is the set of products
// the customer may choose; product is required only when there is a choice to
// make (more than one option). At least one environment must be selected.
export const buildTicketValidationSchema = (productOptions = PRODUCTS) => Yup.object({
  description: Yup.string()
    .transform((value) => (typeof value === 'string' ? value.trim() : value))
    .required('Description is required'),
  urgency: Yup.string()
    .oneOf(URGENCIES, 'Urgency is required')
    .required('Urgency is required'),
  environments: Yup.array()
    .of(Yup.string().oneOf(ENVIRONMENTS))
    .min(1, 'Select at least one environment')
    .required('Select at least one environment'),
  product: productOptions.length > 1
    ? Yup.string().oneOf(productOptions, 'Product is required').required('Product is required')
    : Yup.string().oneOf([...productOptions, ''], 'Invalid product').notRequired()
})

// Backward-compatible default schema (all products allowed).
export const ticketValidationSchema = buildTicketValidationSchema()

// Build a well-formed ticket record ready to persist (Req 1.1, 1.2)
export const buildTicketRecord = ({
  customerId,
  customerName,
  authorEmail,
  description,
  urgency,
  attachments,
  environments,
  product
}) => {
  const now = new Date()
  return {
    customerId,
    customerName,
    authorEmail,
    description: typeof description === 'string' ? description.trim() : description,
    urgency,
    environments: Array.isArray(environments) ? environments.filter((e) => ENVIRONMENTS.includes(e)) : [],
    product: PRODUCTS.includes(product) ? product : null,
    status: 'Open',
    attachments: Array.isArray(attachments) ? attachments : [],
    createdAt: now,
    updatedAt: now,
    statusChangedAt: now
  }
}
